import { useEffect, useState } from "react";
import { api, API, formatApiErrorDetail } from "../lib/api";
import { CodeBlock } from "../components/CodeBlock";
import { Layout } from "../components/Layout";
import { toast } from "sonner";
import {
  Lightning, FloppyDisk, Archive, FileCode, Terminal, Eye, AppleLogo, WifiHigh, BatteryHigh,
  MagnifyingGlass, SpeakerHigh, CaretLeft, Snowflake, ClipboardText, Keyboard, Info,
} from "@phosphor-icons/react";

const REHIDE = [{ v: 0, l: "Smart" }, { v: 1, l: "Timed" }, { v: 2, l: "Focused app" }];
const ICEBAR = [{ v: 0, l: "Dynamic" }, { v: 1, l: "Mouse pointer" }, { v: 2, l: "Ice icon" }];
const TINT = [{ v: 0, l: "None" }, { v: 1, l: "Solid" }, { v: 2, l: "Gradient" }];
const SHAPE = [{ v: 0, l: "None (full width)" }, { v: 1, l: "Full" }, { v: 2, l: "Split" }];
const ENDCAP = [{ v: 1, l: "Round" }, { v: 0, l: "Square" }];

const DEFAULTS = {
  // general
  show_ice_icon: true, custom_ice_icon_is_template: false, use_ice_bar: false, ice_bar_location: 0,
  show_on_click: true, show_on_hover: false, show_on_scroll: true, item_spacing_offset: 0,
  auto_rehide: true, rehide_strategy: 0, rehide_interval: 15,
  // advanced
  hide_application_menus: true, show_section_dividers: false, enable_always_hidden_section: false,
  can_toggle_always_hidden_section: true, show_on_hover_delay: 0.2, temp_show_interval: 15,
  show_all_sections_on_user_drag: true, show_context_menu_on_right_click: true,
  // appearance
  appearance_enabled: false, shadow: false, border: false, border_width: 1, border_color: "000000",
  tint_kind: 0, tint_color: "0a84ff", gradient_start: "0a84ff", gradient_end: "5e5ce6",
  shape_kind: 0, full_leading: 1, full_trailing: 1, inset: true, dynamic: false,
  dark_shadow: false, dark_border: false, dark_border_width: 1, dark_border_color: "ffffff",
  dark_tint_kind: 0, dark_tint_color: "0a84ff", dark_gradient_start: "0a84ff", dark_gradient_end: "5e5ce6",
  // install / system spacing
  install_ice: true, ice_zip_url: "", system_spacing_enabled: false, item_spacing: 16, selection_padding: 16,
};

const HOTKEYS = [
  "Toggle the hidden section", "Toggle the always-hidden section", "Show the Ice Bar",
  "Toggle section dividers", "Toggle application menus", "Search menu bar items", "Temporarily show hidden items",
];

const TABS = [
  { id: "general", label: "General" },
  { id: "appearance", label: "Appearance" },
  { id: "advanced", label: "Advanced" },
  { id: "hotkeys", label: "Hotkeys" },
  { id: "install", label: "Install & Spacing" },
];

export default function MenuBarForge() {
  const [providers, setProviders] = useState([]);
  const [projects, setProjects] = useState([]);
  const [provider, setProvider] = useState("jamf");
  const [v, setV] = useState(DEFAULTS);
  const [tab, setTab] = useState("general");
  const [result, setResult] = useState(null);
  const [tabIdx, setTabIdx] = useState(0);
  const [building, setBuilding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bundling, setBundling] = useState(false);
  const [saveTarget, setSaveTarget] = useState("new");

  useEffect(() => {
    api.get("/providers").then((r) => setProviders(r.data)).catch(() => {});
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  }, []);

  const set = (k, val) => setV((s) => ({ ...s, [k]: val }));

  const build = async () => {
    setBuilding(true); setResult(null);
    try {
      const { data } = await api.post("/menubar/build", { provider, params: v });
      setResult(data); setTabIdx(0);
      toast.success("Generated — 0 tokens used");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Build failed");
    } finally { setBuilding(false); }
  };

  const artifacts = result?.artifacts || [];
  const active = artifacts[tabIdx];

  const save = async () => {
    if (!active) return;
    setSaving(true);
    try {
      if (saveTarget === "new") {
        const { data } = await api.post("/projects", {
          title: `MenuBar Forge (${active.label})`, provider: result.provider,
          output_type: active.type === "profile" ? "profile" : "script",
          prompt: "MenuBar Forge", content: active.content, language: active.language,
          filename: active.filename, deploy_notes: result.deploy_notes,
        });
        setProjects((p) => [data, ...p]);
        toast.success(`Saved ${active.label} as new project`);
      } else {
        const { data } = await api.post(`/projects/${saveTarget}/versions`, {
          content: active.content, language: active.language, filename: active.filename,
          deploy_notes: result.deploy_notes, prompt: "MenuBar Forge", note: `MenuBar Forge (${active.label})`,
        });
        setProjects((list) => list.map((p) => (p.id === data.id ? data : p)));
        toast.success(`Saved new version to "${data.title}"`);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const downloadBundle = async () => {
    if (!artifacts.length) return;
    setBundling(true);
    try {
      const resp = await fetch(`${API}/bundle-multi`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ title: "MenuBar Forge", provider: result.provider, deploy_notes: result.deploy_notes,
          files: artifacts.map((a) => ({ filename: a.filename, content: a.content })) }),
      });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${result.provider}-menubar-forge.zip`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Bundle downloaded");
    } catch { toast.error("Bundle download failed"); } finally { setBundling(false); }
  };

  const copyJson = async () => {
    try { await navigator.clipboard.writeText(JSON.stringify({ provider, params: v }, null, 2)); toast.success("Config copied"); }
    catch { toast.error("Copy failed — clipboard blocked"); }
  };
  const importJson = () => {
    const raw = window.prompt("Paste a MenuBar Forge config JSON:");
    if (!raw) return;
    try { const d = JSON.parse(raw); if (d.provider) setProvider(d.provider); if (d.params) setV((s) => ({ ...s, ...d.params })); toast.success("Config imported"); }
    catch { toast.error("Invalid JSON"); }
  };

  const inputCls = "w-full bg-[#050505] border border-[#27272A] px-3 py-2 text-sm font-mono focus:border-[#00F0FF] focus:outline-none transition-colors duration-200";
  const labelCls = "block font-mono text-[10px] text-[#71717A] mb-1.5 uppercase tracking-wider";

  const Toggle = ({ k, label }) => (
    <button type="button" data-testid={`mb-${k}`} onClick={() => set(k, !v[k])}
      className={`flex items-center gap-2 px-3 py-2 border text-xs text-left transition-colors duration-200 ${v[k] ? "border-[#00FF41] text-[#00FF41] bg-[#00FF41]/5" : "border-[#27272A] text-[#71717A]"}`}>
      <span className={`w-8 h-4 rounded-full relative shrink-0 transition-colors duration-200 ${v[k] ? "bg-[#00FF41]/40" : "bg-[#27272A]"}`}>
        <span className={`absolute top-0.5 w-3 h-3 rounded-full transition-all duration-200 ${v[k] ? "left-4 bg-[#00FF41]" : "left-0.5 bg-[#71717A]"}`} />
      </span>
      {label}
    </button>
  );

  const Sel = ({ k, opts }) => (
    <select data-testid={`mb-${k}`} value={v[k]} onChange={(e) => set(k, Number(e.target.value))} className={inputCls}>
      {opts.map((o) => (<option key={o.v} value={o.v}>{o.l}</option>))}
    </select>
  );

  // preview
  const gap = 14 + Math.max(-10, Math.min(10, Number(v.item_spacing_offset) || 0));
  const tintOn = v.appearance_enabled && v.tint_kind !== 0;
  const tintStyle = !tintOn ? { background: "#1c1c1f" }
    : v.tint_kind === 1 ? { background: `#${v.tint_color}80` }
    : { background: `linear-gradient(90deg,#${v.gradient_start}80,#${v.gradient_end}80)` };
  const rounded = v.appearance_enabled && v.shape_kind !== 0 && v.full_leading === 1;
  const barBorder = v.appearance_enabled && v.border ? `${v.border_width}px solid #${v.border_color}` : undefined;
  const barShadow = v.appearance_enabled && v.shadow ? "0 3px 8px rgba(0,0,0,0.5)" : "none";
  const StatusIcon = ({ children }) => <span className="flex items-center" style={{ color: "#e5e5e7" }}>{children}</span>;

  return (
    <Layout>
      <div className="border-b border-[#27272A] px-8 py-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl tracking-tight flex items-center gap-2">
            <Snowflake size={22} weight="fill" className="text-[#00F0FF]" /> MenuBar Forge
          </h1>
          <p className="text-sm text-[#A1A1AA] font-mono mt-1">
            Full visual builder for Ice (jordanbaird/Ice) — every General, Appearance & Advanced setting, 0 LLM tokens.
          </p>
        </div>
        <span className="hidden md:flex items-center gap-2 font-mono text-xs text-[#00FF41] border border-[#00FF41]/40 bg-[#00FF41]/5 px-3 py-1.5">
          <Lightning size={13} weight="fill" /> 0 LLM TOKENS
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-px bg-[#27272A]">
        <div className="bg-[#0A0A0B] p-8 space-y-6">
          <div>
            <label className={labelCls}>MDM Provider</label>
            <div className="flex flex-wrap gap-2">
              {providers.map((p) => (
                <button key={p.id} data-testid={`mb-provider-${p.id}`} onClick={() => setProvider(p.id)}
                  className={`flex items-center gap-2 px-3 py-2 border text-xs transition-colors duration-200 ${provider === p.id ? "border-[#00F0FF] text-[#00F0FF]" : "border-[#27272A] text-[#A1A1AA] hover:border-[#3f3f46]"}`}>
                  <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />{p.name}
                </button>
              ))}
            </div>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap gap-1 border-b border-[#27272A]">
            {TABS.map((t) => (
              <button key={t.id} data-testid={`mb-tab-${t.id}`} onClick={() => setTab(t.id)}
                className={`px-4 py-2 text-xs font-mono border-b-2 -mb-px transition-colors ${tab === t.id ? "border-[#00F0FF] text-[#00F0FF]" : "border-transparent text-[#71717A] hover:text-[#A1A1AA]"}`}>
                {t.label}
              </button>
            ))}
          </div>

          {/* GENERAL */}
          {tab === "general" && (
            <div className="space-y-5" data-testid="mb-pane-general">
              <div>
                <label className={labelCls}>Show hidden items when you…</label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <Toggle k="show_on_click" label="Click empty space" />
                  <Toggle k="show_on_hover" label="Hover" />
                  <Toggle k="show_on_scroll" label="Scroll / swipe" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <Toggle k="show_ice_icon" label="Show the Ice menu bar icon" />
                <Toggle k="custom_ice_icon_is_template" label="Icon is a template (tinted)" />
                <Toggle k="use_ice_bar" label="Use the Ice Bar for hidden items" />
              </div>
              {v.use_ice_bar && (
                <div><label className={labelCls}>Ice Bar location</label><Sel k="ice_bar_location" opts={ICEBAR} /></div>
              )}
              <div>
                <label className={labelCls}>Item spacing offset ({v.item_spacing_offset})</label>
                <input data-testid="mb-item_spacing_offset" type="range" min="-16" max="16" value={v.item_spacing_offset}
                  onChange={(e) => set("item_spacing_offset", Number(e.target.value))} className="w-full accent-[#00F0FF]" />
                <p className="text-[10px] text-[#71717A] font-mono mt-1">Ice's own spacing offset applied to menu bar items (0 = default).</p>
              </div>
              <div>
                <Toggle k="auto_rehide" label="Automatically re-hide items" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                  <div><label className={labelCls}>Re-hide strategy</label><Sel k="rehide_strategy" opts={REHIDE} /></div>
                  {v.rehide_strategy === 1 && (
                    <div><label className={labelCls}>Re-hide delay (seconds)</label>
                      <input data-testid="mb-rehide_interval" type="number" min="1" value={v.rehide_interval} onChange={(e) => set("rehide_interval", Number(e.target.value))} className={inputCls} /></div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* APPEARANCE */}
          {tab === "appearance" && (
            <div className="space-y-5" data-testid="mb-pane-appearance">
              <Toggle k="appearance_enabled" label="Deploy menu bar appearance (tint / border / shadow / shape)" />
              {!v.appearance_enabled && <p className="text-[10px] text-[#71717A] font-mono">Enable to include the appearance configuration in the output. Leave off to let users control their own look.</p>}
              {v.appearance_enabled && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div><label className={labelCls}>Tint</label><Sel k="tint_kind" opts={TINT} /></div>
                    <div><label className={labelCls}>Shape</label><Sel k="shape_kind" opts={SHAPE} /></div>
                  </div>
                  {v.tint_kind === 1 && (
                    <div className="flex items-center gap-2"><span className="text-[10px] font-mono text-[#71717A]">Tint color</span>
                      <input type="color" data-testid="mb-tint_color" value={`#${v.tint_color}`} onChange={(e) => set("tint_color", e.target.value.replace("#", ""))} className="w-10 h-9 bg-transparent border border-[#27272A] cursor-pointer" /></div>
                  )}
                  {v.tint_kind === 2 && (
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2"><span className="text-[10px] font-mono text-[#71717A]">From</span>
                        <input type="color" data-testid="mb-gradient_start" value={`#${v.gradient_start}`} onChange={(e) => set("gradient_start", e.target.value.replace("#", ""))} className="w-10 h-9 bg-transparent border border-[#27272A] cursor-pointer" /></div>
                      <div className="flex items-center gap-2"><span className="text-[10px] font-mono text-[#71717A]">to</span>
                        <input type="color" data-testid="mb-gradient_end" value={`#${v.gradient_end}`} onChange={(e) => set("gradient_end", e.target.value.replace("#", ""))} className="w-10 h-9 bg-transparent border border-[#27272A] cursor-pointer" /></div>
                    </div>
                  )}
                  {v.shape_kind !== 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div><label className={labelCls}>Leading end cap</label><Sel k="full_leading" opts={ENDCAP} /></div>
                      <div><label className={labelCls}>Trailing end cap</label><Sel k="full_trailing" opts={ENDCAP} /></div>
                    </div>
                  )}
                  <div className="flex flex-wrap items-center gap-2">
                    <Toggle k="shadow" label="Shadow" />
                    <Toggle k="border" label="Border" />
                    <Toggle k="inset" label="Inset menu bar" />
                    <Toggle k="dynamic" label="Dynamic (light/dark)" />
                  </div>
                  {v.border && (
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2"><span className="text-[10px] font-mono text-[#71717A]">Color</span>
                        <input type="color" data-testid="mb-border_color" value={`#${v.border_color}`} onChange={(e) => set("border_color", e.target.value.replace("#", ""))} className="w-10 h-9 bg-transparent border border-[#27272A] cursor-pointer" /></div>
                      <div className="flex items-center gap-2 flex-1"><span className="text-[10px] font-mono text-[#71717A]">Width {v.border_width}</span>
                        <input type="range" min="1" max="5" data-testid="mb-border_width" value={v.border_width} onChange={(e) => set("border_width", Number(e.target.value))} className="flex-1 accent-[#00F0FF]" /></div>
                    </div>
                  )}
                  {v.dynamic && (
                    <div className="border border-[#27272A] bg-[#0f0f11] p-4 space-y-3" data-testid="mb-dark-panel">
                      <div className="font-mono text-[10px] uppercase tracking-wider text-[#B37FEB]">Dark mode overrides</div>
                      <div><label className={labelCls}>Dark tint</label><Sel k="dark_tint_kind" opts={TINT} /></div>
                      {v.dark_tint_kind === 1 && (
                        <div className="flex items-center gap-2"><span className="text-[10px] font-mono text-[#71717A]">Tint color</span>
                          <input type="color" data-testid="mb-dark_tint_color" value={`#${v.dark_tint_color}`} onChange={(e) => set("dark_tint_color", e.target.value.replace("#", ""))} className="w-10 h-9 bg-transparent border border-[#27272A] cursor-pointer" /></div>
                      )}
                      {v.dark_tint_kind === 2 && (
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-2"><span className="text-[10px] font-mono text-[#71717A]">From</span>
                            <input type="color" data-testid="mb-dark_gradient_start" value={`#${v.dark_gradient_start}`} onChange={(e) => set("dark_gradient_start", e.target.value.replace("#", ""))} className="w-10 h-9 bg-transparent border border-[#27272A] cursor-pointer" /></div>
                          <div className="flex items-center gap-2"><span className="text-[10px] font-mono text-[#71717A]">to</span>
                            <input type="color" data-testid="mb-dark_gradient_end" value={`#${v.dark_gradient_end}`} onChange={(e) => set("dark_gradient_end", e.target.value.replace("#", ""))} className="w-10 h-9 bg-transparent border border-[#27272A] cursor-pointer" /></div>
                        </div>
                      )}
                      <div className="flex flex-wrap items-center gap-2">
                        <Toggle k="dark_shadow" label="Shadow" />
                        <Toggle k="dark_border" label="Border" />
                      </div>
                      {v.dark_border && (
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2"><span className="text-[10px] font-mono text-[#71717A]">Color</span>
                            <input type="color" data-testid="mb-dark_border_color" value={`#${v.dark_border_color}`} onChange={(e) => set("dark_border_color", e.target.value.replace("#", ""))} className="w-10 h-9 bg-transparent border border-[#27272A] cursor-pointer" /></div>
                          <div className="flex items-center gap-2 flex-1"><span className="text-[10px] font-mono text-[#71717A]">Width {v.dark_border_width}</span>
                            <input type="range" min="1" max="5" data-testid="mb-dark_border_width" value={v.dark_border_width} onChange={(e) => set("dark_border_width", Number(e.target.value))} className="flex-1 accent-[#00F0FF]" /></div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ADVANCED */}
          {tab === "advanced" && (
            <div className="space-y-4" data-testid="mb-pane-advanced">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <Toggle k="hide_application_menus" label="Hide app menus when showing items" />
                <Toggle k="show_section_dividers" label="Show section divider icons" />
                <Toggle k="enable_always_hidden_section" label="Enable always-hidden section" />
                <Toggle k="can_toggle_always_hidden_section" label="Allow toggling always-hidden" />
                <Toggle k="show_all_sections_on_user_drag" label="Show all sections on ⌘-drag" />
                <Toggle k="show_context_menu_on_right_click" label="Right-click context menu" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><label className={labelCls}>Show-on-hover delay (seconds)</label>
                  <input data-testid="mb-show_on_hover_delay" type="number" step="0.1" min="0" value={v.show_on_hover_delay} onChange={(e) => set("show_on_hover_delay", Number(e.target.value))} className={inputCls} /></div>
                <div><label className={labelCls}>Temporarily-show interval (seconds)</label>
                  <input data-testid="mb-temp_show_interval" type="number" min="1" value={v.temp_show_interval} onChange={(e) => set("temp_show_interval", Number(e.target.value))} className={inputCls} /></div>
              </div>
            </div>
          )}

          {/* HOTKEYS */}
          {tab === "hotkeys" && (
            <div className="space-y-3" data-testid="mb-pane-hotkeys">
              <div className="flex items-start gap-2 border border-[#27272A] bg-[#0f0f11] p-3">
                <Info size={16} className="text-[#00F0FF] mt-0.5 shrink-0" />
                <p className="text-[11px] text-[#A1A1AA] font-mono leading-relaxed">
                  Ice supports global hotkeys for the actions below. Keyboard shortcuts are recorded per-device inside Ice's own Hotkeys pane and aren't set via MDM — deploy Ice with these settings, then have users assign shortcuts, or pre-record them in a golden image.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {HOTKEYS.map((h) => (
                  <div key={h} className="flex items-center gap-2 border border-[#27272A] px-3 py-2 text-xs text-[#A1A1AA]">
                    <Keyboard size={14} className="text-[#71717A]" /> {h}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* INSTALL */}
          {tab === "install" && (
            <div className="space-y-5" data-testid="mb-pane-install">
              <div>
                <label className={labelCls}>Install Ice</label>
                <div className="flex flex-col md:flex-row gap-3">
                  <Toggle k="install_ice" label="Include the installer in the script" />
                  <input data-testid="mb-ice_zip_url" value={v.ice_zip_url} onChange={(e) => set("ice_zip_url", e.target.value)}
                    placeholder="Ice.app .zip URL (host your signed build)" className={inputCls + " flex-1"} />
                </div>
              </div>
              <div>
                <Toggle k="system_spacing_enabled" label="Also set system-wide menu bar spacing (NSStatusItem)" />
                {v.system_spacing_enabled && (
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <div>
                      <div className="flex items-center justify-between mb-1"><span className="text-xs text-[#A1A1AA]">Item spacing</span><span className="font-mono text-xs text-[#00F0FF]">{v.item_spacing}</span></div>
                      <input data-testid="mb-item_spacing" type="range" min="0" max="30" value={v.item_spacing} onChange={(e) => set("item_spacing", Number(e.target.value))} className="w-full accent-[#00F0FF]" />
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1"><span className="text-xs text-[#A1A1AA]">Selection padding</span><span className="font-mono text-xs text-[#00F0FF]">{v.selection_padding}</span></div>
                      <input data-testid="mb-selection_padding" type="range" min="0" max="24" value={v.selection_padding} onChange={(e) => set("selection_padding", Number(e.target.value))} className="w-full accent-[#00F0FF]" />
                    </div>
                  </div>
                )}
                <p className="text-[10px] text-[#71717A] font-mono mt-2">System spacing affects every menu bar item and requires re-login.</p>
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-3 pt-2">
            <button data-testid="mb-build-btn" onClick={build} disabled={building}
              className="flex items-center gap-2 bg-[#00F0FF] text-[#0A0A0B] font-medium px-6 py-3 text-sm hover:bg-[#00d4e0] transition-colors duration-200 disabled:opacity-50">
              <Lightning size={16} weight="fill" /> {building ? "Building…" : "Build config (0 tokens)"}
            </button>
            <button data-testid="mb-import-btn" onClick={importJson} className="flex items-center gap-2 border border-[#27272A] text-[#A1A1AA] px-4 py-3 text-sm hover:border-[#00F0FF] hover:text-[#00F0FF] transition-colors duration-200"><ClipboardText size={16} /> Import</button>
            <button data-testid="mb-export-btn" onClick={copyJson} className="flex items-center gap-2 border border-[#27272A] text-[#A1A1AA] px-4 py-3 text-sm hover:border-[#00F0FF] hover:text-[#00F0FF] transition-colors duration-200"><ClipboardText size={16} /> Copy</button>
          </div>

          {result && active && (
            <div className="space-y-4" data-testid="mb-output">
              <div className="flex border border-[#27272A] w-fit">
                {artifacts.map((a, i) => (
                  <button key={a.filename} data-testid={`mb-arttab-${a.type}`} onClick={() => setTabIdx(i)}
                    className={`flex items-center gap-2 px-4 py-2 text-sm ${i > 0 ? "border-l border-[#27272A]" : ""} ${tabIdx === i ? "bg-[#00F0FF]/10 text-[#00F0FF]" : "text-[#A1A1AA]"}`}>
                    {a.type === "profile" ? <FileCode size={15} /> : <Terminal size={15} />} {a.label}
                  </button>
                ))}
              </div>
              <div className="flex flex-col md:flex-row gap-3">
                <select data-testid="mb-save-target" value={saveTarget} onChange={(e) => setSaveTarget(e.target.value)} className={inputCls + " flex-1"}>
                  <option value="new">＋ New project</option>
                  {projects.map((p) => (<option key={p.id} value={p.id}>↳ Add version to: {p.title}</option>))}
                </select>
                <div className="flex gap-3">
                  <button data-testid="mb-save-btn" onClick={save} disabled={saving} className="flex items-center gap-2 border border-[#00FF41] text-[#00FF41] px-4 py-2 text-sm hover:bg-[#00FF41]/10 transition-colors duration-200 disabled:opacity-50"><FloppyDisk size={16} /> {saving ? "…" : `Save ${active.label}`}</button>
                  <button data-testid="mb-bundle-btn" onClick={downloadBundle} disabled={bundling} className="flex items-center gap-2 border border-[#0A84FF] text-[#0A84FF] px-4 py-2 text-sm hover:bg-[#0A84FF]/10 transition-colors duration-200 disabled:opacity-50"><Archive size={16} /> {bundling ? "…" : "Bundle all"}</button>
                </div>
              </div>
              <CodeBlock code={active.content} language={active.language} filename={active.filename} />
            </div>
          )}
        </div>

        {/* Live preview */}
        <div className="bg-[#0A0A0B] p-8">
          <label className={labelCls + " flex items-center gap-1.5"}><Eye size={12} /> Live menu-bar preview</label>
          <div className="mt-3 rounded-md overflow-hidden border border-[#27272A]" style={{ background: "linear-gradient(135deg,#1e3a5f,#0b1622)" }} data-testid="mb-preview">
            <div className="px-2 pt-2">
              <div className="flex items-center justify-between text-[11px]"
                style={{ ...tintStyle, height: 26, borderRadius: rounded ? 10 : 0, paddingLeft: 8, paddingRight: 8, boxShadow: barShadow, border: barBorder }}>
                <div className="flex items-center" style={{ gap: 12, color: "#e5e5e7" }}>
                  <AppleLogo size={13} weight="fill" />
                  {!v.hide_application_menus && (<><span className="font-semibold">Finder</span><span>File</span><span>Edit</span></>)}
                </div>
                <div className="flex items-center" style={{ gap: `${gap}px` }}>
                  {v.show_section_dividers && <StatusIcon><CaretLeft size={12} weight="bold" style={{ opacity: 0.6 }} /></StatusIcon>}
                  <StatusIcon><SpeakerHigh size={13} /></StatusIcon>
                  <StatusIcon><WifiHigh size={13} /></StatusIcon>
                  <StatusIcon><BatteryHigh size={15} /></StatusIcon>
                  <StatusIcon><MagnifyingGlass size={13} /></StatusIcon>
                  {v.show_ice_icon && <StatusIcon><Snowflake size={13} weight="fill" style={{ color: "#bfe9ff" }} /></StatusIcon>}
                  <span className="font-medium">9:41</span>
                </div>
              </div>
            </div>
            {v.use_ice_bar && (
              <div className="flex justify-end px-2 pb-2 pt-1">
                <div className="flex items-center gap-3 bg-[#1c1c1f]/90 border border-white/10 rounded-md px-3 py-1.5" data-testid="mb-icebar">
                  <SpeakerHigh size={12} className="text-[#e5e5e7]" /><WifiHigh size={12} className="text-[#e5e5e7]" /><BatteryHigh size={13} className="text-[#e5e5e7]" />
                </div>
              </div>
            )}
            <div className="h-24" />
          </div>

          <div className="mt-4 space-y-1.5 text-[11px] font-mono">
            {[
              ["Reveal", [v.show_on_click && "click", v.show_on_hover && "hover", v.show_on_scroll && "scroll"].filter(Boolean).join(" · ") || "manual"],
              ["Auto re-hide", v.auto_rehide ? (REHIDE.find((r) => r.v === v.rehide_strategy)?.l || "on") : "off"],
              ["Ice Bar", v.use_ice_bar ? (ICEBAR.find((i) => i.v === v.ice_bar_location)?.l || "on") : "off"],
              ["Appearance", v.appearance_enabled ? `${TINT.find((t) => t.v === v.tint_kind)?.l} tint · ${SHAPE.find((s) => s.v === v.shape_kind)?.l}` : "not managed"],
              ["Always-hidden section", v.enable_always_hidden_section ? "enabled" : "off"],
              ["Install Ice", v.install_ice ? "yes" : "no"],
            ].map(([k, val]) => (
              <div key={k} className="flex items-center justify-between"><span className="text-[#71717A]">{k}</span><span className="text-[#FAFAFA]">{val}</span></div>
            ))}
          </div>
          <p className="text-[10px] text-[#71717A] font-mono mt-4 leading-relaxed">
            Approximate preview. Keys map 1:1 to Ice's <span className="text-[#A1A1AA]">com.jordanbaird.Ice</span> preferences. Requires macOS 14+. The deploy script writes Ice's own prefs in the user context; the profile forces them via MDM.
          </p>
        </div>
      </div>
    </Layout>
  );
}
